import { AnimatePresence, motion } from "motion/react";
import BottomPanel from "../../app/panels/BottomPanel";
import {
  closeSettings,
  useSettingsSheet,
  type SettingsView,
} from "../../../domains/app/stores/settingsSheetStore";
import { RootView } from "./views/RootView";
import { AccountView } from "./views/AccountView";
import { DeleteConfirmView } from "./views/DeleteConfirmView";
import { MainSpecialtyView } from "./views/MainSpecialtyView";
import { LanguageView } from "./views/LanguageView";
import { NotificationsView } from "./views/NotificationsView";
import { InterestsView } from "./views/InterestsView";
import { PracticeView } from "./views/PracticeView";
import { EditProfileView } from "./views/EditProfileView";
import { EditAvatarView } from "./views/EditAvatarView";
import { CertificationView } from "./views/CertificationView";
import { RoomsView } from "./views/RoomsView";
import { LeaveRoomView } from "./views/LeaveRoomView";
import { LegalView } from "./views/LegalView";
import { PartnerSharingView } from "./views/PartnerSharingView";
import { PartnersView } from "./views/PartnersView";
import { JuisciPrivacyView } from "./views/JuisciPrivacyView";

// Reuse the AudioPlayer morph easing so the settings stack feels part of the
// same DS 5.0 motion language.
const SLIDE_TRANSITION = {
  type: "tween",
  duration: 0.28,
  ease: [0.32, 0.72, 0, 1],
} as const;

const SLIDE_VARIANTS = {
  enter: (direction: "push" | "pop") => ({
    x: direction === "push" ? "100%" : "-100%",
  }),
  center: { x: "0%" },
  exit: (direction: "push" | "pop") => ({
    x: direction === "push" ? "-100%" : "100%",
  }),
};

function renderView(view: SettingsView): React.JSX.Element {
  switch (view.view) {
    case "root":
      return <RootView />;
    case "account":
      return <AccountView />;
    case "deleteConfirm":
      return <DeleteConfirmView />;
    case "mainSpecialty":
      return <MainSpecialtyView />;
    case "language":
      return <LanguageView />;
    case "notifications":
      return <NotificationsView />;
    case "interests":
      return <InterestsView />;
    case "practice":
      return <PracticeView />;
    case "editProfile":
      return <EditProfileView />;
    case "editAvatar":
      return <EditAvatarView />;
    case "certification":
      return <CertificationView />;
    case "rooms":
      return <RoomsView />;
    case "partnerSharing":
      return <PartnerSharingView />;
    case "partners":
      return <PartnersView />;
    case "juisciPrivacy":
      return <JuisciPrivacyView />;
    case "leaveRoom":
      return <LeaveRoomView roomId={view.roomId} roomName={view.roomName} />;
    case "terms":
      return <LegalView doc="terms" tab={view.tab} />;
    case "privacy":
      return <LegalView doc="privacy" tab={view.tab} />;
    default:
      throw new Error(view satisfies never);
  }
}

/**
 * App-wide settings bottom sheet with in-sheet navigation. Mounted once (near
 * `AppToaster`). Desktop web opens `/settings/*` in the desktop settings modal
 * (`DesktopSettingsHost`), so on desktop the sheet only opens through explicit
 * deep links (`openSettingsAt`).
 */
export function SettingsSheet(): React.JSX.Element | null {
  const { open, stack, direction } = useSettingsSheet();

  const active = stack[stack.length - 1];
  if (active === undefined) {
    throw new Error("should not happen: empty settings view stack");
  }

  return (
    <BottomPanel isOpen={open} onClose={closeSettings} height="92dvh">
      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence custom={direction} initial={false}>
          <motion.div
            key={active.view}
            custom={direction}
            variants={SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={SLIDE_TRANSITION}
            className="absolute inset-0 flex flex-col"
          >
            {renderView(active)}
          </motion.div>
        </AnimatePresence>
      </div>
    </BottomPanel>
  );
}
